import ReactApexChart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import { useTranslation } from 'react-i18next'

// Polished port of the old MUI/ApexCharts <PaymentMethodRevenue/> polar-area chart.
// Equal angles, radius scaled by value, with a custom breakdown legend (amount + share).

interface Props {
  title?: string
  subheader?: string
  series: { label: string; value: number }[]
  colors?: string[]
}

const DEFAULT_COLORS = ['#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#F97316']

const fmtAED = (val: number) => `AED ${Number(val).toLocaleString()}`

export function PaymentMethodRevenue({ title, subheader, series, colors }: Props) {
  const { t } = useTranslation()
  const chartSeries = series.map((i) => i.value)
  const labels = series.map((i) => i.label)
  const palette = colors ?? DEFAULT_COLORS
  const total = series.reduce((sum, i) => sum + i.value, 0)

  const options: ApexOptions = {
    chart: { type: 'polarArea', toolbar: { show: false } },
    colors: palette,
    labels,
    stroke: { colors: ['#fff'], width: 1 },
    fill: { opacity: 0.8 },
    yaxis: { show: false },
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      polarArea: {
        rings: { strokeColor: '#F1F3F4' },
        spokes: { connectorColors: '#F1F3F4' },
      },
    },
    tooltip: {
      fillSeriesColor: false,
      y: { formatter: (val: number) => fmtAED(val) },
    },
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {subheader && <p className="text-sm text-gray-400">{subheader}</p>}
        </div>
        <div className="text-right">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{t('dash.totalLabel')}</p>
          <p className="text-lg font-bold text-gray-900">{fmtAED(total)}</p>
        </div>
      </div>

      {/* Chart + breakdown */}
      <div className="flex flex-1 flex-col items-center gap-4 sm:flex-row sm:items-center">
        <div className="w-full max-w-[280px] shrink-0 [&_.apexcharts-datalabels-group]:!hidden">
          <ReactApexChart type="polarArea" series={chartSeries} options={options} height={260} width="100%" />
        </div>

        <ul className="w-full flex-1 space-y-3">
          {series.map((item, idx) => {
            const pct = total > 0 ? (item.value / total) * 100 : 0
            const color = palette[idx % palette.length]
            return (
              <li key={item.label}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                    {item.label}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">{fmtAED(item.value)}</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
